// WP3 — the onboarding-wizard state machine (spec §8). Pure leaf: zod + same-
// package relative imports only (bare-node-testable, the glossary/funnel-events
// convention). The D1 column `sites.onboarding_json` stores exactly
// OnboardingState; parseOnboardingState is the mandatory read boundary
// (CLAUDE.md JSON-column rule) and degrades corrupt blobs to null — a broken
// row must read as "never enrolled", never as a 500.
//
// Event discipline: applyStep's `firstCompletion` is the ONE signal the API
// layer uses to decide whether to record wizard_step_completed — a step
// transitions out of 'pending' exactly once, so a re-save (going back, or a
// double-click) can never double-count the funnel.
import { z } from 'zod';
import type { LayerKey } from './glossary.js';

// Local duplicate of WIZARD_STEPS from funnel-events to keep this module
// testable under node --test (no runtime imports from other modules, only
// type imports like glossary above which erase at runtime).
export const ONBOARDING_STEPS = ['foundation', 'visibility', 'reputation', 'pulse'] as const satisfies readonly LayerKey[];

export const StepStatusSchema = z.enum(['pending', 'done', 'skipped']);
export type StepStatus = z.infer<typeof StepStatusSchema>;

const StepStateSchema = z.object({
  status: StepStatusSchema,
  completedAt: z.string().datetime().nullable(),
});
export type StepState = z.infer<typeof StepStateSchema>;

export const OnboardingStateSchema = z.object({
  version: z.literal(1),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  setupEmailSentAt: z.string().datetime().nullable().default(null),
  steps: z.object({
    foundation: StepStateSchema,
    visibility: StepStateSchema,
    reputation: StepStateSchema,
    pulse: StepStateSchema,
  }),
});
export type OnboardingState = z.infer<typeof OnboardingStateSchema>;

export function emptyOnboardingState(now: Date): OnboardingState {
  const pending: StepState = { status: 'pending', completedAt: null };
  return {
    version: 1,
    startedAt: now.toISOString(),
    completedAt: null,
    setupEmailSentAt: null,
    steps: {
      foundation: { ...pending },
      visibility: { ...pending },
      reputation: { ...pending },
      pulse: { ...pending },
    },
  };
}

export function parseOnboardingState(raw: unknown): OnboardingState | null {
  const parsed = OnboardingStateSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export function applyStep(
  state: OnboardingState,
  step: LayerKey,
  skipped: boolean,
  now: Date,
): { state: OnboardingState; firstCompletion: boolean } {
  const firstCompletion = state.steps[step].status === 'pending';
  return {
    firstCompletion,
    state: {
      ...state,
      steps: {
        ...state.steps,
        [step]: { status: skipped ? 'skipped' : 'done', completedAt: now.toISOString() },
      },
    },
  };
}

export function firstIncompleteStep(state: OnboardingState): LayerKey | null {
  for (const step of ONBOARDING_STEPS) {
    if (state.steps[step].status === 'pending') return step;
  }
  return null;
}

export function isWizardComplete(state: OnboardingState): boolean {
  return firstIncompleteStep(state) === null;
}

export function countSkipped(state: OnboardingState): number {
  return ONBOARDING_STEPS.filter((s) => state.steps[s].status === 'skipped').length;
}

export function completeWizard(
  state: OnboardingState,
  now: Date,
):
  | { ok: true; state: OnboardingState; stepsSkipped: number }
  | { ok: false; error: 'steps_pending' } {
  if (!isWizardComplete(state)) return { ok: false, error: 'steps_pending' };
  return {
    ok: true,
    // idempotent: a second complete keeps the original completedAt stamp
    state: state.completedAt ? state : { ...state, completedAt: now.toISOString() },
    stepsSkipped: countSkipped(state),
  };
}
